import { useRef, type ComponentProps, type ComponentType, type ReactNode } from 'react'
import {
  useWalkTimeline,
  useWalkAbsolute,
  useWalkWrapper,
  useWalkRatio,
  useWalkRatioAbsolute,
} from '~/api/hooks/useCharts'
import { ChartCanvas } from './ChartCanvas'
import { SummaryBarStrip, type PeriodBar } from './SummaryBarStrip'
import { Box, Alert, Stack, Typography } from '@mui/material'
import { getInlineMessage, errorString } from '~/errors/handleApiError'
import type { PinV1 } from '~/pin/PinV1'
import { replayUnitOf } from '~/pin/replayUnit'
import { DimNotApplicableError } from '~/api/types'
import { DimNotApplicableNote } from '~/components/ReviewPage/DimNotApplicableNote'
import { CampaignFilterNote } from '~/components/ReviewPage/CampaignFilterNote'
import { useTimeframeFanout } from '~/api/hooks/useTimeframeFanout'
import { useDriverArmResolution } from './useDriverArmResolution'
import { ExportMenu } from '~/components/Export/ExportMenu'
import { sanitizeFilename } from '~/pin/exportToJson'
import {
  useDynamicsPanels,
  type DynamicsTimeframe,
  type DynamicsWalkFamily,
} from '~/api/hooks/useDynamicsPanels'
import { TimelineChart } from './TimelineChart'
import { PanelStatRow, type PanelStat } from './PanelStatRow'
import { computeDomain, type GranularityUnit } from './dataPipeline'
import { MultiTimeframeOverlayChart } from './MultiTimeframeOverlayChart'
import { fyTickFormatterForRow, fyNoteLabelsForRow } from './fyRowFormatters'
import { buildFyTickFormatter } from './fyTickFormatter'
import { foldRowsToUnit } from './foldWalkRows'
import { PartialFyNote } from '~/components/ReviewPage/PartialFyNote'
import { useKpiDisplayLabel } from '~/components/ReviewPage/useKpiDisplayLabel'
import { useSeriesBaseLabels } from '~/components/ReviewPage/useSeriesBaseLabels'
import { ChartCardHeader } from './chrome/ChartCardHeader'
import { ChartContextCaption } from './chrome/ChartContextCaption'
import {
  buildChartTitle,
  buildContextCaption,
  KICKER_BY_CHART_TYPE,
  CHROME,
  MESSAGES,
} from './chrome/chartChrome'
import { useDimValueLabelFor } from '~/components/ReviewPage/useDimValueLabelFor'
import { ChartLoadingState } from '~/components/ReviewPage/ChartLoadingState'
import { MetricArmChart } from '~/components/ReviewPage/MetricArmChart'
import { resolveArmColoring } from './resolveArmColoring'
import { useDimValues } from '~/api/hooks/useDimValues'
import { useDimValueColorForBase } from './useDimValueColorForBase'
import { useValueTypeLabels } from '~/api/hooks/useValueTypeLabels'
import { useStore } from '~/state/useStore'
import { useSeriesAdditivity } from './useSeriesAdditivity'
import { distinctValueTypes } from './seriesAdditivity'
import { downgradeFacet } from './facets'

type ChartArm = ComponentType<{ pin: PinV1 }>

// D5 (fy-v2-pins-export spec §31): the URL never affects a pin. ChartCanvas falls back
// to ambient gran_unit/gran_dots when the props are absent — pin arms render through
// PinReplayCanvas, which makes both REQUIRED.
const PIN_REPLAY_DOTS = 91

// FU-48: D5 prop discipline made STRUCTURAL (same shape as the chart-semantics wave's
// required `additivity`). ChartCanvas's own `unit`/`dots` are optional with an ambient-URL
// fallback — correct on live Review pages, a silent D5 regression on a pin arm that forgets
// them. Pin arms therefore render ChartCanvas ONLY through this wrapper, where both are
// REQUIRED: pass `unit={replayUnitOf(pin)}` and `dots={PIN_REPLAY_DOTS}` explicitly.
type PinReplayCanvasProps = Omit<ComponentProps<typeof ChartCanvas>, 'unit' | 'dots'> & {
  unit: GranularityUnit
  dots: number
}
function PinReplayCanvas(props: PinReplayCanvasProps) {
  return <ChartCanvas {...props} />
}

// Dispatch by `${walk_call.function}:${render_hint.chart_type}`.
// Add new walk + chart pairs here; the public ChartFromPin signature
// stays stable.
// ChartCanvas-based arms MUST render via PinReplayCanvas (FU-48).
// Forward-references to *FromPin work because they are function declarations (hoisted).
// If converting any to const arrow-expressions, move chartDispatch below the declarations.
const chartDispatch: Record<string, ChartArm> = {
  'walk_timeline:timeline': TimelineFromPin,
  'walk_absolute:absolute_bar': AbsoluteFromPin,
  'walk_wrapper:wrapper': WrapperFromPin,
  'walk_ratio:timeline': RatioTimelineFromPin,
  'walk_ratio_absolute:absolute_bar': RatioAbsoluteFromPin,
  'walk_timeline:summary_bar_strip': SummaryStripFromPin,
  'walk_ratio_absolute:metric_driver_bars': MetricDriverBarsFromPin,
  'walk_absolute:metric_driver_bars': MetricDriverBarsFromPin,
  'walk_ratio:dynamics_panels': DynamicsPanelsFromPin,
  'walk_timeline:dynamics_panels': DynamicsPanelsFromPin,
  'walk_timeline:multi_timeframe_overlay': MultiTimeframeOverlayFromPin,
  'walk_ratio:multi_timeframe_overlay': MultiTimeframeOverlayFromPin,
}

// Live dispatch-key list. Imported by buildPinV1.test.ts so the test stays
// in sync with chartDispatch — adding/removing an arm here is reflected
// automatically in any consumer that asserts dispatch-key membership.
export const CHART_DISPATCH_KEYS = Object.keys(chartDispatch)

// The chart_types whose FromPin arm fans out into MULTIPLE walks (per timeframe and/or a driver
// arm). This is derived from the dispatch → component map, so it is the authoritative source of
// "which chart_types are multi-walk". fetchPinData MUST route every dispatch key carrying one of
// these chart_types through its multi-walk path; the export dispatch-parity test cross-checks
// fetchPinData.MULTI_KEYS against THIS set (an independent source), so a brand-new multi-walk
// chart_type left out of fetchPinData is caught rather than silently flattened to a wrong shape.
const MULTI_WALK_ARMS: ReadonlySet<ChartArm> = new Set([
  MetricDriverBarsFromPin,
  DynamicsPanelsFromPin,
  MultiTimeframeOverlayFromPin,
])
export const MULTI_WALK_CHART_TYPES: ReadonlySet<string> = new Set(
  Object.entries(chartDispatch)
    .filter(([, arm]) => MULTI_WALK_ARMS.has(arm))
    .map(([key]) => key.split(':')[1]!),
)

// Concern (Task 4): spec assumes `pin.id` (UUID), but PinV1 has no `id` field
// (frontend/src/pin/PinV1.ts) — only the wrapping graph_collection row carries
// one. Plan licensed a fingerprint of walk_call. Full JSON.stringify ensures
// unique ids for pins with same kpi+dates but different filters/group_by/etc.
// Result is verbose (e.g. `c_pin:{"function":"walk_timeline","fact_table":...}`)
// but URL-safe after `encodeURIComponent` (which `useSearchParams` applies).
// Follow-up: add `id?: string` to PinV1 and thread `result.row.id` through
// ChartFromPin's props; replace this fingerprint when threading lands. If
// URL length becomes a real-world problem, switch to a hash-based ID.
function derivePinId(pin: PinV1): string {
  return `pin:${JSON.stringify(pin.walk_call)}`
}

/** Chrome for EVERY pin arm (rows 4/5/25 on replay): title from the pin's display label +
 *  date-range phrase (pins carry only from/to — no preset label exists in walk_call), caption
 *  from the frozen filter_dims/group_by. valueTypeLabel stays null: useKpiDisplayLabel may
 *  already carry "(vt)" — never double-unit (spec §2). A saved render_hint.title (PinButton
 *  custom titles + seeded narrative titles, UI wave 2) takes the title slot; the auto-built
 *  KPI · range phrase then leads the caption so provenance stays visible. */
function PinChrome({ pin, children }: { pin: PinV1; children: ReactNode }) {
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const valueLabelFor = useDimValueLabelFor(pin.walk_call.fact_table)
  const autoTitle = buildChartTitle({
    kpiTitle: kpiLabel,
    valueTypeLabel: null,
    // filter_dims and group_by are required fields on PinV1 params (no ?? needed)
    timeframePhrase: `${pin.walk_call.params.from}${CHROME.rangeJoiner}${pin.walk_call.params.to}`,
  })
  const savedTitle = pin.render_hint.title?.trim() || undefined
  const contextLines = buildContextCaption({
    filters: pin.walk_call.params.filter_dims,
    groupBy: pin.walk_call.params.group_by,
    valueLabelFor,
  })
  const lines = savedTitle ? [autoTitle, ...contextLines] : contextLines
  const tileRef = useRef<HTMLDivElement>(null)
  return (
    <Stack spacing={1} ref={tileRef} data-export-pin="true">
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE[pin.render_hint.chart_type] ?? CHROME.kickerTimeline}
        title={savedTitle ?? autoTitle}
        actions={
          <ExportMenu
            pin={pin}
            target={() => tileRef.current}
            filenameBase={sanitizeFilename(savedTitle ?? autoTitle)}
          />
        }
      />
      {children}
      <ChartContextCaption lines={lines} />
    </Stack>
  )
}

export function ChartFromPin({ pin }: { pin: PinV1 }) {
  const key = `${pin.walk_call.function}:${pin.render_hint.chart_type}`
  const Arm = chartDispatch[key]
  if (Arm)
    return (
      <PinChrome pin={pin}>
        <Arm pin={pin} />
      </PinChrome>
    )
  return (
    <Alert severity="warning">
      {MESSAGES.unsupportedPinEntry(pin.walk_call.function, pin.render_hint.chart_type)}
    </Alert>
  )
}

function TimelineFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const seriesBaseLabelFor = useSeriesBaseLabels(
    pin.walk_call.fact_table,
    pin.walk_call.params.group_by ?? [],
  )
  // T7: VT display labels on pin legends. get_controls is already cached per pin fact —
  // PinChrome's useKpiDisplayLabel call fires the same usePageControls(fact_table) query
  // key — so this is a cache hit, not a new fetch.
  const vtLabels = useValueTypeLabels(pin.walk_call.fact_table)
  // Color catalog (spec D3) — UNCONDITIONAL. Pin's walk_call carries both fact_table and
  // group_by (PinV1 schema: both required, non-optional — see MetricDriverBarsFromPin above).
  const colorForBase = useDimValueColorForBase(
    pin.walk_call.params.group_by,
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
  )
  const walk = useWalkTimeline({
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
    // Frozen-snapshot: FE-canonical defaults so a pin saved without these
    // renders consistently regardless of DB-function default changes.
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
    aggregation: pin.walk_call.params.aggregation ?? 'sum',
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  // Replay value_type filter stored in the pin (absent on older pins → show all).
  const savedVts = pin.walk_call.params.value_types
  const rows =
    savedVts && savedVts.length > 0
      ? (walk.data ?? []).filter((r) => r.value_type == null || savedVts.includes(r.value_type))
      : (walk.data ?? [])
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    distinctValueTypes(rows),
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <PinReplayCanvas
          pinId={derivePinId(pin)}
          chartType="timeline"
          rows={rows}
          groupBy={pin.walk_call.params.group_by ?? []}
          yLabel={kpiLabel}
          seriesBaseLabelFor={seriesBaseLabelFor}
          vtLabels={vtLabels}
          colorForBase={colorForBase}
          unit={replayUnit}
          dots={PIN_REPLAY_DOTS}
          queryFromIso={pin.walk_call.params.from}
          queryToIso={pin.walk_call.params.to}
          additivity={additivity}
        />
      ) : null}
    </Box>
  )
}

function AbsoluteFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const seriesBaseLabelFor = useSeriesBaseLabels(
    pin.walk_call.fact_table,
    pin.walk_call.params.group_by ?? [],
  )
  // T7: VT display labels on pin legends (get_controls cache hit — see TimelineFromPin above).
  const vtLabels = useValueTypeLabels(pin.walk_call.fact_table)
  // Color catalog (spec D3) — UNCONDITIONAL (see TimelineFromPin above).
  const colorForBase = useDimValueColorForBase(
    pin.walk_call.params.group_by,
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
  )
  const walk = useWalkAbsolute({
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
    // Frozen-snapshot: FE-canonical defaults so a pin saved without these
    // renders consistently regardless of DB-function default changes.
    aggregation: pin.walk_call.params.aggregation ?? 'sum',
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  // Replay value_type filter stored in the pin (absent on older pins → show all).
  const savedVts = pin.walk_call.params.value_types
  const rows =
    savedVts && savedVts.length > 0
      ? (walk.data ?? []).filter((r) => r.value_type == null || savedVts.includes(r.value_type))
      : (walk.data ?? [])
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    distinctValueTypes(rows),
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <PinReplayCanvas
          pinId={derivePinId(pin)}
          chartType="absolute_bar"
          rows={rows}
          groupBy={pin.walk_call.params.group_by ?? []}
          yLabel={kpiLabel}
          seriesBaseLabelFor={seriesBaseLabelFor}
          vtLabels={vtLabels}
          colorForBase={colorForBase}
          unit={replayUnit}
          dots={PIN_REPLAY_DOTS}
          queryFromIso={pin.walk_call.params.from}
          queryToIso={pin.walk_call.params.to}
          additivity={additivity}
        />
      ) : null}
    </Box>
  )
}

function WrapperFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  // T7: VT display labels on pin legends (get_controls cache hit — see TimelineFromPin above).
  const vtLabels = useValueTypeLabels(pin.walk_call.fact_table)
  const walk = useWalkWrapper({
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
    // walk_wrapper has no aggregation knob; long_format_dims defaults to []
    // server-side, so no frozen-snapshot needed here.
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  // WalkWrapperRow carries no value_type field at all (distinctValueTypes has nothing to read),
  // and ChartCanvas short-circuits to WrapperChart before the verdict has any visible effect —
  // pass [] directly rather than force a structurally-incompatible distinctValueTypes call.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    [],
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <PinReplayCanvas
          pinId={derivePinId(pin)}
          chartType="wrapper"
          rows={walk.data}
          groupBy={pin.walk_call.params.group_by ?? []}
          yLabel={kpiLabel}
          vtLabels={vtLabels}
          unit={replayUnit}
          dots={PIN_REPLAY_DOTS}
          queryFromIso={pin.walk_call.params.from}
          queryToIso={pin.walk_call.params.to}
          additivity={additivity}
        />
      ) : null}
    </Box>
  )
}

// Ratio replay arms: the kpi is the ratio identifier (passed as p_ratio_kpi inside
// the hook). pin.walk_call.fact_table is informational for ratios and deliberately
// NOT forwarded — the ratio RPC resolves its own component facts.
function RatioTimelineFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const seriesBaseLabelFor = useSeriesBaseLabels(
    pin.walk_call.fact_table,
    pin.walk_call.params.group_by ?? [],
  )
  // T7: VT display labels on pin legends (get_controls cache hit — see TimelineFromPin above).
  const vtLabels = useValueTypeLabels(pin.walk_call.fact_table)
  // Color catalog (spec D3) — UNCONDITIONAL. pin.walk_call.fact_table is informational for
  // ratios (not forwarded to the walk hook — see comment above) but still names the fact
  // whose dims_view catalog the group-by dim's values live in, same as the live ChartTile
  // ratio branch (state.factTable is used there regardless of isRatio).
  const colorForBase = useDimValueColorForBase(
    pin.walk_call.params.group_by,
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
  )
  const walk = useWalkRatio({
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  const savedVts = pin.walk_call.params.value_types
  const rows =
    savedVts && savedVts.length > 0
      ? (walk.data ?? []).filter((r) => r.value_type == null || savedVts.includes(r.value_type))
      : (walk.data ?? [])
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  // fact_table is informational-only for ratios (see comment above) but is still the fact whose
  // dims_view catalog the group-by dim lives in — same reasoning as colorForBase.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    distinctValueTypes(rows),
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <PinReplayCanvas
          pinId={derivePinId(pin)}
          chartType="timeline"
          rows={rows}
          groupBy={pin.walk_call.params.group_by ?? []}
          yLabel={kpiLabel}
          seriesBaseLabelFor={seriesBaseLabelFor}
          vtLabels={vtLabels}
          isRatio
          colorForBase={colorForBase}
          unit={replayUnit}
          dots={PIN_REPLAY_DOTS}
          queryFromIso={pin.walk_call.params.from}
          queryToIso={pin.walk_call.params.to}
          additivity={additivity}
        />
      ) : null}
    </Box>
  )
}

function RatioAbsoluteFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const seriesBaseLabelFor = useSeriesBaseLabels(
    pin.walk_call.fact_table,
    pin.walk_call.params.group_by ?? [],
  )
  // Color catalog (spec D3) — UNCONDITIONAL (see RatioTimelineFromPin above).
  const colorForBase = useDimValueColorForBase(
    pin.walk_call.params.group_by,
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
  )
  // T7: VT display labels on pin legends (get_controls cache hit — see TimelineFromPin above).
  const vtLabels = useValueTypeLabels(pin.walk_call.fact_table)
  const walk = useWalkRatioAbsolute({
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  const savedVts = pin.walk_call.params.value_types
  const rows =
    savedVts && savedVts.length > 0
      ? (walk.data ?? []).filter((r) => r.value_type == null || savedVts.includes(r.value_type))
      : (walk.data ?? [])
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    distinctValueTypes(rows),
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <PinReplayCanvas
          pinId={derivePinId(pin)}
          chartType="absolute_bar"
          rows={rows}
          groupBy={pin.walk_call.params.group_by ?? []}
          yLabel={kpiLabel}
          seriesBaseLabelFor={seriesBaseLabelFor}
          vtLabels={vtLabels}
          colorForBase={colorForBase}
          unit={replayUnit}
          dots={PIN_REPLAY_DOTS}
          queryFromIso={pin.walk_call.params.from}
          queryToIso={pin.walk_call.params.to}
          additivity={additivity}
        />
      ) : null}
    </Box>
  )
}

// Metric|driver side-by-side bars (WP-2 slice 2). Re-fires the multi-timeframe fan-out
// for the metric walk (ratio or absolute) and an optional driver walk, then renders each
// arm via MetricArmChart with a shared color map (ranked rows, same colors across arms).
// Both useTimeframeFanout calls are UNCONDITIONAL and in fixed order (hooks rules);
// the empty-driver case passes [] as timeframes (disabled).
function MetricDriverBarsFromPin({ pin }: { pin: PinV1 }) {
  const vp = (pin.render_hint.viz_params ?? {}) as {
    driver_kpi?: string
    driver_fact?: string
    driver_value_type?: string
    // Campaigns C1 Task 8: entries may carry a FROZEN per-arm filter_dims (Task 7's
    // ResolvedTimeframe shape) — the value is already threaded through unmodified into
    // useTimeframeFanout below (tf.filter_dims ?? spec.filter_dims lives inside the hook)
    // for the METRIC fanout. FU-03: the DRIVER fanout receives a fact-stripped copy of
    // these per-arm values instead (see the strip block below) — only the driver arm is
    // pruned to the driver fact's dims; the metric arm always stays unmodified.
    timeframes?: Array<{
      id: string
      label: string
      from: string
      to: string
      filter_dims?: Record<string, string[]>
    }>
  }
  const kpiLabel = useKpiDisplayLabel(pin.walk_call.fact_table, pin.walk_call.kpi)
  const driverLabel = useKpiDisplayLabel(
    vp.driver_fact ?? pin.walk_call.fact_table,
    vp.driver_kpi ?? pin.walk_call.kpi,
  )
  // Render-only: maps raw series dim-value keys -> display labels for the bar legends (row 8)
  const seriesBaseLabelFor = useSeriesBaseLabels(
    pin.walk_call.fact_table,
    pin.walk_call.params.group_by,
  )
  const timeframes = vp.timeframes ?? [
    { id: 'main', label: '', from: pin.walk_call.params.from, to: pin.walk_call.params.to },
  ]
  // FU-27(a): driver-arm resolution (fact-valid filter strip, dims=null fallback, campaign-block
  // spec decision 2) is shared via useDriverArmResolution — see its doc comment for the full
  // rule. Catalog PENDING → the driver fanout is gated (empty timeframes — useTimeframeFanout's
  // own disabled idiom; it has NO single-window mode and takes no `enabled` knob). TanStack v5
  // disabled queries report isLoading:false, so an enabled-style gate would not wedge anything —
  // the empty-timeframes form is simply the hook's native convention (the dynamics arm, whose
  // hook IS dual-mode, must gate `enabled` too — see DynamicsPanelsFromPin).
  const hasDriver = Boolean(vp.driver_kpi)
  const { driverGate, baseDriver, driverTimeframes, campaignBlocked } = useDriverArmResolution(
    vp.driver_fact ?? pin.walk_call.fact_table,
    hasDriver,
    pin.walk_call.params.filter_dims,
    timeframes,
  )
  const metric = useTimeframeFanout(
    pin.walk_call.function === 'walk_ratio_absolute'
      ? {
          family: 'ratio_absolute',
          kpi: pin.walk_call.kpi,
          filter_dims: pin.walk_call.params.filter_dims,
          group_by: pin.walk_call.params.group_by,
        }
      : {
          family: 'absolute',
          kpi: pin.walk_call.kpi,
          fact_table: pin.walk_call.fact_table,
          filter_dims: pin.walk_call.params.filter_dims,
          group_by: pin.walk_call.params.group_by,
        },
    timeframes,
  )
  const driver = useTimeframeFanout(
    vp.driver_kpi
      ? {
          family: 'absolute',
          kpi: vp.driver_kpi,
          fact_table: vp.driver_fact!,
          filter_dims: baseDriver?.filters ?? pin.walk_call.params.filter_dims,
          group_by: pin.walk_call.params.group_by,
        }
      : {
          family: 'absolute',
          kpi: pin.walk_call.kpi,
          fact_table: pin.walk_call.fact_table,
          filter_dims: {},
          group_by: [],
        },
    hasDriver && !driverGate && !campaignBlocked ? driverTimeframes : [],
  )
  // Color catalog (spec D3) — UNCONDITIONAL. Pin's walk_call cleanly carries both
  // fact_table and group_by (PinV1 schema: both required, non-optional), so the replay
  // arm can wire the same single-group-by-dim catalog the live tile uses. Multiple/zero
  // group-by dims fall back to catalogValues = [] (overflow-only, old locale-sorted colors).
  const groupByDim =
    pin.walk_call.params.group_by.length === 1 ? pin.walk_call.params.group_by[0]! : ''
  const colorCatalog = useDimValues(groupByDim, pin.walk_call.fact_table, pin.walk_call.kpi)
  const palette = useStore((s) => s.personal.chipPalette)
  const metricError = metric.byTimeframe.find((s) => s.isError)?.error ?? null
  // FU-m (D3): a typed driver error renders the precise group-by note in the driver slot
  // (instead of a silent-empty chart). The metric arm is the pinned primary — unchanged.
  const driverError = driver.byTimeframe.find((s) => s.isError)?.error ?? null
  if (metric.isError) {
    const msg = errorString(metricError) ?? MESSAGES.chartLoadFailed
    return <Alert severity="warning">{msg}</Alert>
  }
  // T8: guard against the F1 "No data" flash while either fanout is still in flight
  // (mirrors the live MetricDriverBarsTile.armContent per-arm isLoading guard).
  if (metric.isLoading || driver.isLoading || driverGate) return <ChartLoadingState />
  const { colorForKey, smallMultiples } = resolveArmColoring(
    metric.byTimeframe,
    vp.driver_kpi ? driver.byTimeframe : null,
    timeframes.length,
    groupByDim ? colorCatalog.data : [],
    palette,
  )
  return (
    <Stack direction={smallMultiples ? 'column' : { xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ flex: 1 }}>
        <MetricArmChart
          label={kpiLabel}
          slices={metric.byTimeframe}
          colorForKey={colorForKey}
          labelForKey={seriesBaseLabelFor}
        />
      </Box>
      {vp.driver_kpi ? (
        <Box sx={{ flex: 1 }}>
          {campaignBlocked ? (
            <CampaignFilterNote metricLabel={driverLabel} />
          ) : driverError instanceof DimNotApplicableError ? (
            <DimNotApplicableNote dims={driverError.dims} metricLabel={driverLabel} />
          ) : (
            <MetricArmChart
              label={driverLabel}
              slices={driver.byTimeframe}
              colorForKey={colorForKey}
              labelForKey={seriesBaseLabelFor}
            />
          )}
        </Box>
      ) : null}
    </Stack>
  )
}

// Merged-Dynamics replay (WP-2 slice 3). Re-fires useDynamicsPanels per slot from frozen
// viz_params. Both calls are UNCONDITIONAL and in fixed order (hooks rules); the empty-driver
// case passes enabled:false. Pins freeze resolved timeframes (single 'main' window when the
// picker was empty), so replay always renders in per-timeframe form. Titles resolve display
// labels via useKpiDisplayLabel (raw kpi while controls load).
function DynamicsPanelsFromPin({ pin }: { pin: PinV1 }) {
  const vp = (pin.render_hint.viz_params ?? {}) as {
    driver_kpi?: string
    driver_fact?: string
    driver_value_type?: string
    timeframes?: DynamicsTimeframe[]
  }
  const driverLabel = useKpiDisplayLabel(
    vp.driver_fact ?? pin.walk_call.fact_table,
    vp.driver_kpi ?? pin.walk_call.kpi,
  )
  const timeframes: DynamicsTimeframe[] =
    vp.timeframes && vp.timeframes.length > 0
      ? vp.timeframes
      : [{ id: 'main', label: '', from: pin.walk_call.params.from, to: pin.walk_call.params.to }]
  const family: DynamicsWalkFamily = pin.walk_call.function === 'walk_ratio' ? 'ratio' : 'timeline'
  const bucketSize = pin.walk_call.params.bucket_size ?? 'month'
  const replayUnit = replayUnitOf(pin)
  // T6b anti-drift pattern: ONE opts object feeds tick formatter AND note labels — they
  // share primitives and cannot disagree. Replay never threads a baseline (see the PanelStat
  // comment below: pins never carry baselineFromIso), so no baselineFromIso here.
  const fyOpts = {
    unit: replayUnit,
    fromIso: pin.walk_call.params.from,
    toIso: pin.walk_call.params.to,
    timeframes,
  }
  const fyTickFor = fyTickFormatterForRow(fyOpts)
  const fyNoteLabels = fyNoteLabelsForRow(fyOpts)

  // FU-27(a): driver-arm resolution (fact-valid filter strip, dims=null fallback, campaign-block
  // spec decision 2) is shared via useDriverArmResolution — see its doc comment for the full
  // rule. Catalog PENDING → the driver call is gated OFF (both `enabled` AND empty timeframes) so
  // it fires exactly once, already stripped: useDynamicsPanels is dual-mode — `enabled:true` +
  // empty timeframes is its SINGLE-WINDOW mode (Hook B's useQuery fires a real walk over the full
  // pin window), not a disabled state, so timeframes:[] alone does not suppress the walk here
  // (unlike useTimeframeFanout, which has no single mode — see MetricDriverBarsFromPin above).
  // TanStack v5 reports isLoading:false for a disabled query, so gating `enabled` off does not
  // wedge the isLoading guard below (the pre-existing no-driver case has always shipped
  // enabled:false the same way).
  const hasDriver = Boolean(vp.driver_kpi)
  const { driverGate, baseDriver, driverTimeframes, campaignBlocked } = useDriverArmResolution(
    vp.driver_fact ?? pin.walk_call.fact_table,
    hasDriver,
    pin.walk_call.params.filter_dims,
    timeframes,
  )

  // Color catalog (spec D3) — UNCONDITIONAL. Keyed on the pinned PRIMARY group_by/fact_table/kpi
  // and shared by both calls below, same as the live DynamicsPanelsTile (single colorForBase fed
  // to metric + driver, not narrowed per-arm) — keeps pin/live series colors identical.
  const colorForBase = useDimValueColorForBase(
    pin.walk_call.params.group_by,
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
  )

  const metric = useDynamicsPanels({
    family,
    kpi: pin.walk_call.kpi,
    factTable: pin.walk_call.fact_table,
    filterDims: pin.walk_call.params.filter_dims,
    groupBy: pin.walk_call.params.group_by,
    bucketSize,
    fromIso: pin.walk_call.params.from,
    toIso: pin.walk_call.params.to,
    unit: replayUnit,
    dots: 7,
    timeframes,
    enabled: true,
    colorForBase,
  })
  const driver = useDynamicsPanels({
    family: 'timeline',
    kpi: vp.driver_kpi ?? pin.walk_call.kpi,
    factTable: vp.driver_fact ?? pin.walk_call.fact_table,
    filterDims: vp.driver_kpi ? (baseDriver?.filters ?? pin.walk_call.params.filter_dims) : {},
    groupBy: vp.driver_kpi ? pin.walk_call.params.group_by : [],
    bucketSize,
    fromIso: pin.walk_call.params.from,
    toIso: pin.walk_call.params.to,
    unit: replayUnit,
    dots: 7,
    timeframes: hasDriver && !driverGate && !campaignBlocked ? driverTimeframes : [],
    enabled: Boolean(vp.driver_kpi) && !driverGate && !campaignBlocked,
    colorForBase,
  })

  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict
  // (Task 12b: closes the pin-replay Dynamics bypass — every other pin arm already gates its
  // facet through this resolver). metric.valueTypes IS distinctValueTypes over the arrived rows
  // (both dual-mode sources merged — see useDynamicsPanels' Task 12 comment); reused directly
  // rather than re-derived, since this layer has no raw WalkTimelineRow[] of its own to run
  // distinctValueTypes over — useDynamicsPanels is the ONE site (FU-02(b) precedent).
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    metric.valueTypes,
  )

  if (metric.isError) {
    const msg = errorString(metric.error) ?? MESSAGES.chartLoadFailed
    return <Alert severity="warning">{msg}</Alert>
  }
  // T8: guard against the F1 "No data" flash while either dynamics fetch is still in flight
  // (mirrors the live MetricDriverBarsTile.armContent per-arm isLoading guard).
  if (metric.isLoading || driver.isLoading || driverGate) return <ChartLoadingState />

  const summaryBars: PeriodBar[] = metric.panels.map((p) => ({
    label: p.label,
    total: p.aggregate,
    growth: p.growth,
  }))
  // metric section renders stacked_area → stacked-sum domain (prevents grouped clip); driver is line.
  const metricDomain = computeDomain(
    metric.panels.map((p) => p.points),
    metric.groups,
    true,
  )
  const driverDomain = computeDomain(
    driver.panels.map((p) => p.points),
    driver.groups,
    false,
  )
  // Task 12c (chart-semantics wave), widened by Task 14 (JL wave-close decision, 2026-08-03):
  // the metric row's own per-series-stats decision — mirrors DynamicsPanelsTile's
  // metricPerSeriesStats (live tile), now ANY non-blessed verdict rather than just
  // vt-heterogeneous. `family` is the walk family, not the hook's `ratioComponents` field
  // (null in per-TF mode regardless of family — see useDynamicsPanels.ts's own doc comment,
  // would misclassify a ratio per-TF metric as non-ratio). Driver stays out of scope here too
  // (no additivity verdict computed for it, matching the live tile).
  const metricPerSeriesStats = family !== 'ratio' && !additivity.additive

  // Wave-3 §2 (D3): Σ/ø/Δ chips promote to panel-aligned PanelStatRows above each chart row.
  // No `baseline` field here — pin replay never threads baselineFromIso into useDynamicsPanels,
  // so these panels never carry isBaseline (matches the prior ControlLabel calls, which never
  // passed a `baseline` prop either).
  const metricStats: PanelStat[] = metric.panels.map((p) => ({
    prefix: p.label,
    // Task 12c: ignored by PanelStatRow whenever `perSeries` is set — zeroed here (mirrors
    // ChartCanvas's Task 5 / the live DynamicsPanelsTile) so no fabricated cross-VT aggregate
    // sits on the object even unread.
    sum: metricPerSeriesStats ? 0 : p.aggregate,
    avg: metricPerSeriesStats ? 0 : p.avg,
    growth: metricPerSeriesStats ? null : p.growth,
    // Task 12c: VT-heterogeneous, non-ratio metric — one labeled Σ per value-type series
    // instead of the fabricated cross-VT total. No baseline slot in pin replay (see above), so
    // no `empty` exclusion is needed here (unlike the live tile's baseline-aware guard).
    ...(metricPerSeriesStats
      ? {
          perSeries: metric.groups.map((g) => ({
            label: g.label,
            sum: p.seriesSums.get(g.key) ?? 0,
            // Dynamics' Δ chain is per-panel-aggregate, not per-series — no chain invented here.
            growth: null,
          })),
        }
      : {}),
  }))
  const driverStats: PanelStat[] = driver.panels.map((p) => ({
    prefix: p.label,
    sum: p.aggregate,
    avg: p.avg,
    growth: p.growth,
  }))

  return (
    <Stack spacing={2}>
      <Box>
        {/* metric row carries no own title — PinChrome's card header already names the kpi (holistic V2 fix) */}
        <PartialFyNote labels={fyNoteLabels} />
        <PanelStatRow stats={metricStats} leadOffset />
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          {metric.panels.map((p, i) => (
            <Box key={p.key} sx={{ flex: 1, minWidth: 0 }}>
              <TimelineChart
                points={p.points}
                groups={metric.groups}
                facet={downgradeFacet('stacked_area', additivity.additive)}
                yDomain={metricDomain}
                hideYAxis={i !== 0}
                tickFormatter={fyTickFor?.(p)}
              />
            </Box>
          ))}
        </Stack>
      </Box>
      {vp.driver_kpi ? (
        // FU-m (D3): a typed driver error renders the precise group-by note (instead of a
        // silent-empty panel row). The metric arm is the pinned primary — unchanged.
        campaignBlocked ? (
          <CampaignFilterNote metricLabel={driverLabel} />
        ) : driver.error instanceof DimNotApplicableError ? (
          <DimNotApplicableNote dims={driver.error.dims} metricLabel={driverLabel} />
        ) : (
          <Box>
            <Typography variant="subtitle2">{driverLabel}</Typography>
            <PanelStatRow stats={driverStats} leadOffset />
            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              {driver.panels.map((p, i) => (
                <Box key={p.key} sx={{ flex: 1, minWidth: 0 }}>
                  <TimelineChart
                    points={p.points}
                    groups={driver.groups}
                    facet="line"
                    yDomain={driverDomain}
                    hideYAxis={i !== 0}
                    tickFormatter={fyTickFor?.(p)}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )
      ) : null}
      {/* Final-review fix (Finding 1, critical): summaryBars carries metric.panels[].aggregate —
          a cross-series total summed across VT series, fabricated whenever this arm's own
          additivity verdict says the group-by/VT selection isn't additive (mirrors the metric
          row's TimelineChart facet downgrade above and SummaryStripFromPin's `additive` prop
          below). Default-deny doctrine (spec 2026-08-03 §1): no total beats a false one — this
          arm has no raw rows to fall back to a per-series strip variant, so a non-additive
          verdict renders no strip at all, rather than attempting toPeriodSeriesPoints without
          rows (which would emit the misleading "no data" empty state instead). */}
      {additivity.additive ? <SummaryBarStrip bars={summaryBars} /> : null}
    </Stack>
  )
}

// Multi-timeframe overlay replay (WP-2 slice 4). Re-fires the per-timeframe timeline fan-out from
// frozen viz_params and overlays the timeframes as N lines. A pin frozen with an empty picker holds a
// single 'main' window → renders as one line (a degenerate overlay, still valid).
function MultiTimeframeOverlayFromPin({ pin }: { pin: PinV1 }) {
  const vp = (pin.render_hint.viz_params ?? {}) as { timeframes?: DynamicsTimeframe[] }
  const timeframes: DynamicsTimeframe[] =
    vp.timeframes && vp.timeframes.length > 0
      ? vp.timeframes
      : [{ id: 'main', label: '', from: pin.walk_call.params.from, to: pin.walk_call.params.to }]
  const family: DynamicsWalkFamily = pin.walk_call.function === 'walk_ratio' ? 'ratio' : 'timeline'
  const replayUnit = replayUnitOf(pin)

  const data = useDynamicsPanels({
    family,
    kpi: pin.walk_call.kpi,
    factTable: pin.walk_call.fact_table,
    filterDims: pin.walk_call.params.filter_dims,
    groupBy: pin.walk_call.params.group_by,
    bucketSize: pin.walk_call.params.bucket_size ?? 'month',
    fromIso: pin.walk_call.params.from,
    toIso: pin.walk_call.params.to,
    unit: replayUnit,
    dots: 7,
    timeframes,
    enabled: true,
  })

  if (data.isError) {
    const msg = errorString(data.error) ?? MESSAGES.chartLoadFailed
    return <Alert severity="warning">{msg}</Alert>
  }
  const series = data.panels.map((p) => ({ id: p.key, label: p.label, points: p.points }))
  return (
    <Box>
      {data.isLoading ? <ChartLoadingState /> : null}
      {!data.isLoading ? <MultiTimeframeOverlayChart series={series} groups={data.groups} /> : null}
    </Box>
  )
}

// Summary overview strip (D1: always walk_timeline). Unlike the chart arms the strip is a
// grand-total-per-period overview and has no value_type replay filter — but "sums ALL rows"
// is now conditional on this arm's own additivity verdict (chart-semantics doctrine, spec
// 2026-08-03 §1): a heterogeneous-VT selection renders grouped per-VT bars instead (Task 11).
// Rows are forwarded unchanged when the display unit equals the wire bucket (every legacy
// pin); at a differing frozen display unit (fy) they fold via foldRowsToUnit first — see the
// inline comment below.
function SummaryStripFromPin({ pin }: { pin: PinV1 }) {
  const replayUnit = replayUnitOf(pin)
  const wireBucket = pin.walk_call.params.bucket_size ?? 'month'
  const walk = useWalkTimeline({
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: pin.walk_call.params.from,
    to: pin.walk_call.params.to,
    filter_dims: pin.walk_call.params.filter_dims,
    group_by: pin.walk_call.params.group_by,
    // Frozen-snapshot: FE-canonical defaults so a pin saved without these
    // renders consistently regardless of DB-function default changes.
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
    aggregation: pin.walk_call.params.aggregation ?? 'sum',
  })
  const inlineMsg = walk.error ? getInlineMessage(walk.error) : null
  // FY v2: fold to the frozen display unit before the strip's own 12-bar partitioning —
  // identity skip when the display unit IS the wire bucket (every legacy pin). D1: the strip
  // is always walk_timeline → volume engine. Labels: FY form via the pin's own window.
  const fold = replayUnit !== wireBucket
  const rows = fold ? foldRowsToUnit(walk.data ?? [], replayUnit, 'volume') : (walk.data ?? [])
  const labelFor =
    replayUnit === 'fy'
      ? buildFyTickFormatter(pin.walk_call.params.from, pin.walk_call.params.to)
      : undefined
  // Chart-semantics doctrine (spec 2026-08-03 §1) — UNCONDITIONAL, this arm's own verdict.
  const additivity = useSeriesAdditivity(
    pin.walk_call.fact_table,
    pin.walk_call.kpi,
    pin.walk_call.params.group_by ?? [],
    distinctValueTypes(rows),
  )

  return (
    <Box>
      {walk.isLoading ? <ChartLoadingState /> : null}
      {inlineMsg ? <Alert severity="warning">{inlineMsg}</Alert> : null}
      {walk.data && !inlineMsg ? (
        <SummaryBarStrip rows={rows} labelFor={labelFor} additive={additivity.additive} />
      ) : null}
    </Box>
  )
}
